import { useEffect, useMemo, useState } from 'react';
import type { GameId } from '@urbanrats/contracts';
import { App } from './App.tsx';
import type { ClientRoot } from './gateway/index.ts';
import { I18nContext, makeI18n, type LocaleCode } from './i18n/index.ts';
import { Lobby } from './Lobby.tsx';
import type { EmbedHandle } from './embed.ts';

const GAME_KEY = 'urbanrats.game.v1';

function loadLocale(): LocaleCode {
  try {
    return (JSON.parse(localStorage.getItem('urbanrats.prefs.v1') ?? '{}') as { locale?: string }).locale === 'tr' ? 'tr' : 'en';
  } catch {
    return 'en';
  }
}

/** Lobby ↔ game screen. The game screen keeps its own i18n/preferences; the lobby reads the saved locale. */
export function Root({ root, embed = null }: { root: ClientRoot; embed?: EmbedHandle | null }) {
  const [gameId, setGameId] = useState<GameId | null>(() => {
    try {
      // A launch may name the game (operator lobby / embed); otherwise the last one played.
      // Real sessions start on the Chaos Lane home unless the launch names the game; the demo shell remembers the last game.
      const v = new URLSearchParams(location.search).get('game') ?? (root.kind === 'demo' ? localStorage.getItem(GAME_KEY) : null);
      return root.games.some((g) => g.gameId === v) ? (v as GameId) : null;
    } catch {
      return null;
    }
  });
  const i18n = useMemo(() => makeI18n(loadLocale()), []);
  useEffect(() => {
    root.activate(gameId);
    try {
      if (gameId) localStorage.setItem(GAME_KEY, gameId);
      else localStorage.removeItem(GAME_KEY);
    } catch {
      /* convenience only */
    }
  }, [root, gameId]);
  if (gameId) return <App key={gameId} gw={root.forGame(gameId)} onLobby={() => setGameId(null)} embed={embed} />;
  return (
    <I18nContext.Provider value={i18n}>
      <div className="app app-home">
        <Lobby root={root} onPlay={setGameId} />
      </div>
    </I18nContext.Provider>
  );
}
